import { isToday as isTodayTz, isYesterday as isYesterdayTz } from "@clipboard-health/date-time";
import { type TuFunction } from "@src/appV2/i18n/types";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { format, isThisYear, isToday, isYesterday } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- @clipboard-health/date-time doesn't export formatInTimeZone equivalent
import { formatInTimeZone } from "date-fns-tz";

const TIME_FORMAT = "h:mm a";
const fallbackTu: TuFunction = (defaultText) => defaultText;

/**
 * Return `timeZone` if it is a valid IANA time zone, otherwise `undefined`.
 *
 * `formatInTimeZone` throws `RangeError` for malformed IANA strings (e.g.
 * whitespace, typos, or zones not recognized by the runtime's Intl data).
 * Guarding the caller here keeps a bad `session.profile.tmz` from crashing
 * chat rendering — we fall back to the viewer's device time zone instead.
 */
function getValidTimeZone(timeZone?: string): string | undefined {
  const trimmed = timeZone?.trim();
  if (!trimmed) {
    return undefined;
  }
  try {
    // Probe Intl directly — cheaper than calling formatInTimeZone and matches
    // the same validation date-fns-tz performs internally.
    // eslint-disable-next-line no-new -- side-effect probe; we only care whether the constructor throws.
    new Intl.DateTimeFormat(undefined, { timeZone: trimmed });
    return trimmed;
  } catch {
    return undefined;
  }
}

/**
 * Format a timestamp for display in chat messages.
 * - Today: "5:42 PM"
 * - Yesterday: "Yesterday 5:42 PM"
 * - This year: "Jan 15 5:42 PM"
 * - Previous years: "Jan 15, 2023 5:42 PM"
 *
 * When `timeZone` is provided, all comparisons and formatting are done in
 * that IANA time zone (e.g. the workplace's local time). When omitted,
 * falls back to the viewer's device time zone.
 */
export function formatMessageTime(timestamp: number, timeZone?: string): string {
  const date = new Date(timestamp);
  const validTimeZone = getValidTimeZone(timeZone);

  if (validTimeZone) {
    const tzOptions = { timeZone: validTimeZone };

    if (isTodayTz(date, tzOptions)) {
      return formatInTimeZone(date, validTimeZone, TIME_FORMAT);
    }

    if (isYesterdayTz(date, tzOptions)) {
      return `Yesterday ${formatInTimeZone(date, validTimeZone, TIME_FORMAT)}`;
    }

    const nowYear = formatInTimeZone(new Date(), validTimeZone, "yyyy");
    const dateYear = formatInTimeZone(date, validTimeZone, "yyyy");

    if (nowYear === dateYear) {
      return formatInTimeZone(date, validTimeZone, `MMM d ${TIME_FORMAT}`);
    }

    return formatInTimeZone(date, validTimeZone, `MMM d, yyyy ${TIME_FORMAT}`);
  }

  if (isToday(date)) {
    return format(date, TIME_FORMAT);
  }

  if (isYesterday(date)) {
    return `Yesterday ${format(date, TIME_FORMAT)}`;
  }

  if (isThisYear(date)) {
    return format(date, `MMM d ${TIME_FORMAT}`);
  }

  return format(date, `MMM d, yyyy ${TIME_FORMAT}`);
}

/**
 * Format a timestamp as a short time-of-day for in-bubble chat timestamps,
 * e.g. "5:42 PM". When `timeZone` is provided, formats in that IANA time
 * zone; otherwise uses the viewer's device time zone.
 */
export function formatMessageBubbleTime(timestamp: number, timeZone?: string): string {
  const date = new Date(timestamp);
  const validTimeZone = getValidTimeZone(timeZone);
  return validTimeZone
    ? formatInTimeZone(date, validTimeZone, TIME_FORMAT)
    : format(date, TIME_FORMAT);
}

/**
 * Format a timestamp for the channel list using the facility timezone.
 * Uses @clipboard-health/date-time for timezone-aware day comparisons
 * and date-fns-tz for timezone-aware formatting.
 *
 * - Today: "5:42 PM"
 * - Yesterday: "Yesterday 5:42 PM"
 * - This year: "Jan 15 5:42 PM"
 * - Previous years: "Jan 15, 2023 5:42 PM"
 *
 * @param timestamp - Unix timestamp in milliseconds
 * @param timeZone - IANA timezone string (e.g., "America/New_York")
 * @returns Formatted time string in the facility timezone
 */
export function formatChannelListTime(timestamp: number, timeZone: string): string {
  return formatMessageTime(timestamp, timeZone);
}

/**
 * Format a date for grouping messages by day.
 * - Today: "Today"
 * - Yesterday: "Yesterday"
 * - This year: "January 15"
 * - Previous years: "January 15, 2023"
 *
 * When `timeZone` is provided, comparisons and formatting are done in that
 * IANA time zone. When omitted, falls back to the viewer's device time zone.
 */
export function formatMessageDate(
  timestamp: number,
  timeZone?: string,
  tu: TuFunction = fallbackTu
): string {
  const date = new Date(timestamp);
  const validTimeZone = getValidTimeZone(timeZone);

  if (validTimeZone) {
    const tzOptions = { timeZone: validTimeZone };

    if (isTodayTz(date, tzOptions)) {
      return tu("Today");
    }

    if (isYesterdayTz(date, tzOptions)) {
      return tu("Yesterday");
    }

    const nowYear = formatInTimeZone(new Date(), validTimeZone, "yyyy");
    const dateYear = formatInTimeZone(date, validTimeZone, "yyyy");

    if (nowYear === dateYear) {
      return formatInTimeZone(date, validTimeZone, "MMMM d");
    }

    return formatInTimeZone(date, validTimeZone, "MMMM d, yyyy");
  }

  if (isToday(date)) {
    return tu("Today");
  }

  if (isYesterday(date)) {
    return tu("Yesterday");
  }

  if (isThisYear(date)) {
    return format(date, "MMMM d");
  }

  return format(date, "MMMM d, yyyy");
}

/**
 * Check if two timestamps are on the same day. When `timeZone` is provided,
 * the day comparison is done in that IANA time zone (e.g. the workplace's
 * local time); otherwise the viewer's device time zone is used.
 */
export function isSameDay(timestamp1: number, timestamp2: number, timeZone?: string): boolean {
  const validTimeZone = getValidTimeZone(timeZone);

  if (validTimeZone) {
    return (
      formatInTimeZone(new Date(timestamp1), validTimeZone, "yyyy-MM-dd") ===
      formatInTimeZone(new Date(timestamp2), validTimeZone, "yyyy-MM-dd")
    );
  }

  const date1 = new Date(timestamp1);
  const date2 = new Date(timestamp2);

  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}
